import { defineStore } from 'pinia';
import { useNuxtApp } from "#app";
import { RecipeDetailType } from "~/types/recipe/RecipeDetailType";
import { timeSpentFormatter } from "~/utils/formatters/date.formatter";
import { setMeta } from "~/utils/meta";
import { _chunk, _isUndefined } from "#imports";

export const useRecipeDetailStore = defineStore({
    id: 'recipeDetail',
    state: (): RecipeDetailType => ({
        title: undefined,
        slug: undefined,
        prepTime: undefined,
        cookingTime: undefined,
        calories: undefined,
        countryOfOrigin: undefined,
        portions: undefined,
        category: undefined,
        mainPicture: undefined,
        ingredientGroups: undefined,
        steps: undefined,
        text: undefined,
        stepsWithText: undefined,
        datePublished: undefined,
        keywords: undefined,
        error: undefined,
    }),
    getters: {
        getPrepTime(state: RecipeDetailType) {
            if (!state.prepTime) {
                return '';
            }

            return timeSpentFormatter(state.prepTime);
        },

        getCookingTime(state: RecipeDetailType) {
            if (!state.cookingTime) {
                return '';
            }

            return timeSpentFormatter(state.cookingTime);
        },

        getTotalTime(state: RecipeDetailType) {
            if (!state.prepTime && !state.cookingTime) {
                return '';
            }

            return timeSpentFormatter(state.prepTime + state.cookingTime);
        },

        getCountryOfOriginFlag(state: RecipeDetailType) {
            if (!state.countryOfOrigin?.flag) {
                return '';
            }

            return state.countryOfOrigin?.flag;
        },

        getStructuredData(state: RecipeDetailType): object[] {
            const recipe: object = {
                "@context": "https://schema.org/",
                "@type": "Recipe",
                "name": state.title,
                "image": [
                    this.$useNuxtApp.imgProxyResize(state.mainPicture, 500)
                ],
                "datePublished": state.datePublished,
                "description": state.text,
                "keywords": state.keywords,
                "recipeCategory": state.category?.title,
            };

            if (state.prepTime) {
                recipe.prepTime = `PT${state.prepTime}M`;
            }

            if (state.cookingTime) {
                recipe.cookTime = `PT${state.cookingTime}M`;
            }

            if (state.prepTime || state.cookingTime) {
                recipe.totalTime = `PT${state.prepTime + state.cookingTime}M`;
            }

            if (state.countryOfOrigin?.title) {
                recipe.recipeCuisine = state.countryOfOrigin?.title;
            }

            if (state.calories) {
                recipe.nutrition = {
                    "@type": "NutritionInformation",
                    "calories": state.calories + " calories",
                };
            }

            if (state.portions) {
                recipe.recipeYield = state.portions;
            }

            if (state.ingredientGroups) {
                recipe.recipeIngredient = [];
                state.ingredientGroups.forEach(ingredientGroup => {
                    ingredientGroup?.ingredients?.forEach(ingredient => {
                        recipe.recipeIngredient.push(
                            `${ingredient.dosage} ${ingredient.title}`.toLowerCase()
                        );
                    });
                })
            }

            if (state.steps) {
                recipe.recipeInstructions = [];
                state.steps.forEach((step, index) => {
                    const stepItem: object = {
                        "@type": "HowToStep",
                        "name": `Step ${index + 1}`,
                        "text": step.text,
                        "url": `${this.$useRuntimeConfig.public.baseUrl}${this.$useRoute.fullPath}#step${index + 1}`,
                    };
                    if (step.picture) {
                        stepItem.image = this.$useNuxtApp.imgProxyResize(step.picture, 500);
                    }

                    recipe.recipeInstructions.push(stepItem);
                });
            }

            return [
                recipe,
                {
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Recipes",
                            "item": this.$useRuntimeConfig.public.baseUrl + "/recipes",
                        },
                        {
                            "@type": "ListItem",
                            "position": 2,
                            "name": state.category?.title,
                            "item": this.$useRuntimeConfig.public.baseUrl + state.category?.url,
                        },
                        {
                            "@type": "ListItem",
                            "position": 3,
                            "name": state.title,
                        }
                    ]
                }
            ];
        },
    },
    actions: {
        clear() {
            this.title = undefined;
            this.slug = undefined;
            this.prepTime = undefined;
            this.cookingTime = undefined;
            this.calories = undefined;
            this.countryOfOrigin = undefined;
            this.portions = undefined;
            this.category = undefined;
            this.mainPicture = undefined;
            this.ingredientGroups = undefined;
            this.steps = undefined;
            this.text = undefined;
            this.stepsWithText = undefined;
            this.datePublished = undefined;
            this.keywords = undefined;
            this.error = undefined;
        },

        async fetch(slug: string) {
            try {
                const response = await useNuxtApp().axios.get('/recipes', {
                    params: {
                        'filters[slug][$eq]': slug,
                        fields: ['slug', 'title', 'prep_time', 'cooking_time', 'calories', 'portions', 'main_text', 'steps_with_text', 'publishedAt'],
                        'populate[recipe_category][populate]': '*',
                        'populate[recipe_country_of_origin][populate]': '*',
                        'populate[main_picture][populate]': '*',
                        'populate[ingredients_group][populate][ingredient][populate]': '*',
                        'populate[steps][populate]': '*',
                        'populate[seo][populate][0]': 'metaImage',
                    }
                });
                if (response?.data?.data === undefined || !response?.data?.data.length) {
                    this.error = createError({ statusCode: 404, statusMessage: 'Page Not Found' });
                    return false;
                }

                const data = response?.data?.data[0];

                this.slug = data.attributes?.slug;
                this.title = data.attributes?.title;
                this.prepTime = Number(data.attributes?.prep_time);
                this.cookingTime = Number(data.attributes?.cooking_time);
                this.calories = Number(data.attributes?.calories);
                this.portions = Number(data.attributes?.portions);
                this.text = data.attributes?.main_text;
                this.stepsWithText = data.attributes?.steps_with_text;
                this.datePublished = data.attributes?.publishedAt;

                if (data.attributes?.recipe_category) {
                    this.category = {
                        slug: data.attributes?.recipe_category?.data?.attributes?.slug,
                        svgInline: data.attributes?.recipe_category?.data?.attributes?.svg_icon,
                        title: data.attributes?.recipe_category?.data?.attributes?.title,
                        url: `/recipes/category-${data.attributes?.recipe_category?.data?.attributes?.slug}`,
                    };
                }

                if (data.attributes?.recipe_country_of_origin) {
                    this.countryOfOrigin = {
                        title: data.attributes?.recipe_country_of_origin?.data?.attributes?.title,
                        slug: data.attributes?.recipe_country_of_origin?.data?.attributes?.slug,
                        flag: data.attributes?.recipe_country_of_origin?.data?.attributes?.flag,
                    };
                }

                if (data?.attributes?.main_picture?.data?.attributes?.url !== undefined) {
                    this.mainPicture = this.$useRuntimeConfig.public.ssrMediaBaseUrl + data?.attributes?.main_picture?.data?.attributes?.url;
                } else {
                    this.mainPicture = '';
                }

                if (data?.attributes?.ingredients_group) {
                    this.ingredientGroups = data?.attributes?.ingredients_group?.map(ingredientGroup => {
                        const group = {
                            title: ingredientGroup?.title,
                            ingredients: [],
                        };

                        ingredientGroup?.ingredient?.forEach(ingredient => {
                            group.ingredients.push({
                                title: ingredient?.recipe_ingredient?.data?.attributes?.title,
                                dosage: ingredient?.dosage && ingredient?.dosage !== 'undefined' ? ingredient?.dosage : '',
                            });
                        });

                        return group;
                    });

                    if (this.ingredientGroups !== undefined && this.ingredientGroups?.length === 1) {
                        const chunks = _chunk(this.ingredientGroups[0].ingredients, Math.ceil(this.ingredientGroups[0].ingredients?.length / 2));
                        this.ingredientGroups[0].ingredients = chunks[0];
                        if (!_isUndefined(chunks[1])) {
                            this.ingredientGroups.push({
                                title: '',
                                ingredients: chunks[1],
                            });
                        }
                    }
                }

                if (data?.attributes?.steps) {
                    this.steps = data?.attributes?.steps?.map(step => {
                        let picture = '';
                        if (step?.picture?.data?.attributes?.url !== undefined) {
                            picture = this.$useRuntimeConfig.public.ssrMediaBaseUrl + step?.picture?.data?.attributes?.url;
                        }
                        return {
                            text: step?.text,
                            picture,
                        };
                    });
                }

                if (data.attributes?.seo !== undefined) {
                    this.keywords = data.attributes?.seo.keywords;
                    if (data.attributes.seo?.structuredData === undefined || data.attributes.seo?.structuredData === null) {
                        data.attributes.seo.structuredData = this.getStructuredData;
                    }

                    setMeta(data.attributes?.seo, this.$useRuntimeConfig, this.$useRoute);
                }
            } catch (error) {
                console.log(error);
                this.error = createError({ statusCode: 500, statusMessage: 'Internal error' });
            }

            return true;
        },
    },
});
